// @noflow
import { Allergen, HealthIssue } from '@/gql/graphql'
import React from 'react'
import { useTranslation } from 'react-i18next'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import {
  WizardAge,
  WizardEaterType
} from '@/components/pages/SignupWizardPage/types/types'
import Image from '@/components/shared/elements/Image/Image'
import PreWizardGuestDogProfileCard from '@/components/shared/elements/PreWizardGuestDogProfileCard/PreWizardGuestDogProfileCard'
import SwiperSlider from '@/components/shared/elements/SwiperSlider/SwiperSlider'
import Text from '@/components/shared/elements/Text/Text'

import STYLES from './DogProfileSection.module.sass'

type PendingSubscriptionData = {
  duration_in_weeks: number
  servings: number
  serving_size: number
  price_per_day: string
}

type DogData = {
  name: string
  age: WizardAge
  breed: {
    id: number
    key: string
    name: string
  }
  weight: number
  appetite: WizardEaterType
  allergies: Array<{ name: string }>
  healthIssues: Array<{ name: string }>
}

type Props = {
  pendingSubscriptionData: PendingSubscriptionData | null
  dogsData: DogData[] | null
  isModal?: boolean
}

const MEAL_INFO_CARDS = [
  {
    imageSlug: 'returning-guest-calendar-yellow',
    text: 'week'
  },
  {
    imageSlug: 'returning-guest-pouch-yellow',
    text: 'meals'
  },
  {
    imageSlug: 'returning-guest-bowl-yellow',
    text: 'price'
  }
]

const DogProfileSection = ({
  pendingSubscriptionData,
  dogsData,
  isModal = false
}: Props): JSX.Element | null => {
  const namespace = 'home_page'
  const copyContext = 'returning_guest'
  const { windowWidth } = useWindowSize()
  const { t } = useTranslation(namespace)

  // Return null if no dogs data exists
  if (!dogsData?.length) {
    return null
  }

  const isMultidog = dogsData.length > 1
  const isMobile = windowWidth <= BREAKPOINTS.md

  // Destructure pendingSubscriptionData values
  const { duration_in_weeks, servings, serving_size, price_per_day } =
    pendingSubscriptionData || {}

  return (
    <div className={STYLES.dogProfileSection}>
      {pendingSubscriptionData && (
        <div
          className={`${STYLES.mealInfoCardContainer} ${
            isModal ? STYLES.mealInfoCardContainerModal : ''
          }`}
        >
          {MEAL_INFO_CARDS.map((card) => (
            <div className={STYLES.mealInfoCard} key={card.text}>
              <Image
                alt={t(`${copyContext}.${card.text}_alt`)}
                slug={card.imageSlug}
                image={{
                  width: 40,
                  height: 40,
                  resizeMode: 'resize_to_fit'
                }}
              />
              <Text
                variant="textRegular16"
                namespace={namespace}
                text={`${copyContext}.${card.text}`}
                variables={{
                  count: duration_in_weeks,
                  durationInWeeks: duration_in_weeks,
                  servings: servings,
                  servingSize: serving_size,
                  pricePerDay: price_per_day
                }}
                margin={false}
                align="center"
              />
            </div>
          ))}
        </div>
      )}
      <div className={STYLES.dogProfileCarouselContainer}>
        <SwiperSlider
          className={STYLES.dogProfileCarousel}
          arrows={!isMobile && isMultidog}
          arrowsInside={!isMobile}
          bullets={isMultidog}
          nextButtonAriaLabel={t(`${copyContext}.next_arrow`)}
          previousButtonAriaLabel={t(`${copyContext}.previous_arrow`)}
          initialSlide={0}
          slidesPerView={!isMobile || !isMultidog ? 1 : undefined}
          loop={!isMobile}
          spaceBetween={8}
          isCentered={!isMultidog && !isMobile}
        >
          {dogsData.map((dog: DogData, index: number) => (
            <div
              key={dog.name}
              className={`${
                isMultidog
                  ? STYLES.dogProfileCarouselMultidogItem
                  : isModal
                  ? STYLES.dogProfileCarouselItemModal
                  : STYLES.dogProfileCarouselItem
              } ${
                index === 0
                  ? STYLES.first && !isModal
                  : index === dogsData.length - 1
                  ? STYLES.last
                  : ''
              }`}
            >
              <div
                className={`${STYLES.dogProfileCarouselItemContent} ${
                  isModal ? STYLES.dogProfileCarouselItemContentModal : ''
                }${
                  !isMultidog && isMobile && !isModal ? STYLES.singleDog : ''
                } ${!isMultidog && isModal ? STYLES.singleDogModal : ''}`}
              >
                <PreWizardGuestDogProfileCard
                  dogName={dog.name}
                  dogAge={dog.age || { weeks: null, months: null, years: null }}
                  breedName={dog.breed?.name || ''}
                  weight={dog.weight || 0}
                  appetite={dog.appetite}
                  allergies={dog.allergies as Array<Allergen>}
                  healthIssues={dog.healthIssues as Array<HealthIssue>}
                  namespace={namespace}
                />
              </div>
            </div>
          ))}
        </SwiperSlider>
      </div>
    </div>
  )
}

export default DogProfileSection
